/**
 * The screen's theme (Dakota, 2026-11-03): Light · Dark · Match device, the person's `ui.theme`
 * preference through `/api/preferences`, with a local copy so the first paint is already right.
 * Light is the default (D-082). The stylesheet holds both sets — `:root` the light one,
 * `:root[data-theme='dark']` the dark one — so a theme is the attribute on `<html>` and nothing else.
 *
 * `index.html` repeats `THEME_STORAGE_KEY` and `themeFor` in a few lines of its own, run before
 * React loads so a dark page never flashes white; `theme.test.ts` holds the two to the same key.
 * The React side — the reconcile with the API, the live device query, the setter the Profile page
 * uses — is `components/ThemeProvider.tsx`.
 */

import { readStoredChoice, type KeyValueStore } from './storedFlag.js';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

/** The choices, as `JobRules.Themes` names them on the API; `system` is Match device. */
export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system'];
export const THEME_DEFAULT: ThemeChoice = 'light';
export const THEME_STORAGE_KEY = 'unitedos.ui.theme';
export const THEME_LABELS: Readonly<Record<ThemeChoice, string>> = { light: 'Light', dark: 'Dark', system: 'Match device' };
/** The device's dark setting, as the browser reports it. */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export function isThemeChoice(raw: unknown): raw is ThemeChoice {
  return typeof raw === 'string' && (THEME_CHOICES as readonly string[]).includes(raw);
}

/** The theme drawn: the choice itself, or the device's when the choice is Match device. */
export function themeFor(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice === 'system') return systemDark ? 'dark' : 'light';
  return choice;
}

/** The local copy, or null when there is none, it is junk, or storage is unavailable. */
export function readStoredTheme(store: KeyValueStore | null): ThemeChoice | null {
  return readStoredChoice(store, THEME_STORAGE_KEY, THEME_CHOICES);
}

/**
 * The choice once the API has answered: the person's saved preference when it is one of the three,
 * else the browser's copy, else light — `resolveFlag`'s order, because the preference is what
 * follows the person from machine to machine.
 */
export function resolveTheme(stored: ThemeChoice | null, remote: unknown): ThemeChoice {
  if (isThemeChoice(remote)) return remote;
  return stored ?? THEME_DEFAULT;
}

/** The minimum of an element this needs, so a test can hand in a plain object. */
export interface ThemeTarget {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/** Dark is `data-theme="dark"`; light is no attribute at all, so `:root` alone draws it. */
export function applyTheme(target: ThemeTarget, theme: Theme): void {
  if (theme === 'dark') target.setAttribute('data-theme', 'dark');
  else target.removeAttribute('data-theme');
}

/** Whether the device asks for dark now; false where the browser cannot say. */
export function deviceIsDark(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_QUERY).matches;
  } catch {
    return false;
  }
}
